import { minutesSinceMidnight } from "@/lib/clock";
import { DAY_KEYS, type DayKey } from "@/lib/days";
import type { WorkdayLogConfig, WorkdayLogDayData } from "@/lib/schema";

/** Minutes worked: zero for a blank day and for one marked "Ikke jobbet". */
export function loggedMinutes(data: WorkdayLogDayData): number {
  if (data.notWorked === true) return 0;
  return minutesSinceMidnight(data.duration) ?? 0;
}

/**
 * How many productivity periods the day earned — one per **whole** period
 * worked (CONTEXT.md: "Periode").
 *
 * Flooring is the whole point of the concept: a one-hour Saturday earns
 * none, so it cannot bank the quality points of a full day (docs/adr/0009).
 * It does put a step at every exact multiple, and at a 07:30 target with
 * 03:30 periods that step sits at 07:00 — half an hour of deliberate slack
 * below the target rather than right at it.
 */
export function earnedPeriods(
  config: WorkdayLogConfig,
  data: WorkdayLogDayData,
): number {
  const period = minutesSinceMidnight(config.periodDuration);
  // Guards the settings preview, which renders while a duration field is
  // mid-edit and momentarily unparseable; validation blocks saving such a
  // config.
  if (period === null || period <= 0) return 0;
  return Math.floor(loggedMinutes(data) / period);
}

/**
 * The hours half: pro rata against the target, floored to the quarter-point
 * grid.
 *
 * The ratio itself is usually not representable — 2p over 07:30 is 4/15,
 * which no binary float holds — so an unfloored score would put
 * 0.26666666666666666 on a day tile and 10.266666666666667 on the week's.
 * Flooring to quarters restores exactness for every one of the 1440 minute
 * values a day can take, which is why nothing outside this widget needs a
 * rounding rule.
 */
export function hoursPoints(
  config: WorkdayLogConfig,
  data: WorkdayLogDayData,
): number {
  const target = minutesSinceMidnight(config.targetDuration);
  if (target === null || target <= 0) return 0;
  if (!Number.isFinite(config.pointsAtTarget)) return 0;
  const raw = (loggedMinutes(data) / target) * config.pointsAtTarget;
  return Math.floor(raw * 4) / 4;
}

/**
 * The quality half: the instance's points for every earned period answered
 * Ja. Answers past the earned count are ignored unconditionally, so a
 * shrinking duration withdraws their points whether or not storage has
 * caught up yet.
 */
export function qualityPoints(
  config: WorkdayLogConfig,
  data: WorkdayLogDayData,
): number {
  if (!Number.isFinite(config.pointsPerPeriod)) return 0;
  const earned = earnedPeriods(config, data);
  const answers = data.periods ?? [];
  let yes = 0;
  for (let i = 0; i < earned; i++) {
    if (answers[i] === true) yes += 1;
  }
  return yes * config.pointsPerPeriod;
}

export function scoreDay(config: WorkdayLogConfig, dayData: unknown): number {
  const data = (dayData ?? {}) as WorkdayLogDayData;
  return hoursPoints(config, data) + qualityPoints(config, data);
}

/** Sum one day-level scorer across the week. */
function sumWeek(
  config: WorkdayLogConfig,
  days: Record<DayKey, unknown>,
  scorer: (config: WorkdayLogConfig, data: WorkdayLogDayData) => number,
): number {
  let total = 0;
  for (const k of DAY_KEYS) {
    total += scorer(config, (days[k] ?? {}) as WorkdayLogDayData);
  }
  return total;
}

/** The Arbeidstid part alone (docs/adr/0010). */
export function scoreWeekHours(
  config: WorkdayLogConfig,
  runtime: { week: unknown; days: Record<DayKey, unknown> },
): number {
  return sumWeek(config, runtime.days, hoursPoints);
}

/** The Produktivitet part alone (docs/adr/0010). */
export function scoreWeekPeriods(
  config: WorkdayLogConfig,
  runtime: { week: unknown; days: Record<DayKey, unknown> },
): number {
  return sumWeek(config, runtime.days, qualityPoints);
}

export function scoreWeek(
  config: WorkdayLogConfig,
  runtime: { week: unknown; days: Record<DayKey, unknown> },
): number {
  let total = 0;
  for (const k of DAY_KEYS) total += scoreDay(config, runtime.days[k]);
  return total;
}

/**
 * Both halves are uncapped — overtime keeps paying pro rata and every whole
 * period earns another question — so a week has no ceiling (docs/adr/0003).
 */
export function maxScoreWeek(): number {
  return Number.POSITIVE_INFINITY;
}
